import { useCallback, useState } from "react";
import { ApiError, sendAction, type RunStarted } from "../api/client";

/**
 * Round-trip de una acción de UI.
 *
 *   POST /api/sessions/{session_id}/actions
 *   { "block_id": "blk_…", "event": "quiz.submit", "payload": { … } }
 *   → 202 { "run_id": "…", "message_id": "…" }
 *
 * El backend persiste la acción como parte `ui_event` y arranca un run nuevo;
 * aquí solo se devuelve el `run_id` para que el llamador se suscriba a su
 * stream. La UI no espera la respuesta del agente: sigue viva y el run llega
 * por SSE como cualquier otro turno.
 */

/** Eventos que el frontend puede emitir (tabla de docs/protocol.md). */
export const BLOCK_EVENTS = {
  quizSubmit: "quiz.submit",
  simulatorChange: "simulator.change",
  flashcardsReviewed: "flashcards.reviewed",
  actionInvoke: "action.invoke",
} as const;

export type BlockEventName =
  | "quiz.submit"
  | "simulator.change"
  | "flashcards.reviewed"
  | "action.invoke";

export interface BlockActionController {
  invoke: (
    blockId: string,
    event: string,
    payload: Record<string, unknown>,
  ) => Promise<RunStarted | null>;
  /** Bloque cuya acción está en vuelo: sirve para deshabilitar su control. */
  pendingBlockId: string | null;
  error: string | null;
  clearError: () => void;
}

export function useBlockAction(
  sessionId: string | null,
  onRunStarted?: (started: RunStarted) => void,
): BlockActionController {
  const [pendingBlockId, setPendingBlockId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const invoke = useCallback(
    async (
      blockId: string,
      event: string,
      payload: Record<string, unknown>,
    ): Promise<RunStarted | null> => {
      if (!sessionId) {
        setError("Todavía no hay sesión con el backend.");
        return null;
      }
      setPendingBlockId(blockId);
      setError(null);
      try {
        const started = await sendAction(sessionId, { block_id: blockId, event, payload });
        onRunStarted?.(started);
        return started;
      } catch (cause) {
        setError(
          cause instanceof ApiError
            ? `No se pudo registrar la acción: ${cause.message}`
            : "No se pudo registrar la acción.",
        );
        return null;
      } finally {
        setPendingBlockId(null);
      }
    },
    [onRunStarted, sessionId],
  );

  const clearError = useCallback(() => setError(null), []);

  return { invoke, pendingBlockId, error, clearError };
}
